import {
  validateVegaLiteCalculateTransforms,
  validateVegaLiteTupleExpression,
  type VegaLiteCalculateContext,
} from './vega-lite-calculate-policy';
import {
  analyzeVegaLiteEventSelector,
  describeVegaLiteExpressionFindings,
} from './vega-lite-expression-policy';
import { escapeJsonPointerSegment, type JsonObject, type JsonValue } from './vega-lite-json';
import { validateVegaLiteJsonTree } from './vega-lite-json-tree';
import { getVegaLiteNumericFormatIssue } from './vega-lite-numeric-format-policy';
import { VEGA_LITE_MAX_RESOURCE_COUNT } from './vega-lite-resource-limits';
import validationMetadata from './vega-lite-validation-metadata.json';
import { VEGA_LITE_DATASET_NAME, VEGA_LITE_WIDGET_LIMITS } from './vega-lite-widget-options';

/**
 * Pinned Vega-Lite contract. The renderer always embeds with `mode: 'vega-lite'`, so the schema is
 * metadata, but keeping it fixed makes exported definitions self-describing and blocks definitions
 * authored against another Vega or Vega-Lite major version.
 */
export const VEGA_LITE_SCHEMA_URL = validationMetadata.schemaUrl;

export const VEGA_LITE_SPEC_LIMITS = {
  maxLayers: validationMetadata.spec.maxLayers,
  maxSpecBytes: VEGA_LITE_WIDGET_LIMITS.maxSpecBytes,
  maxTransforms: 32,
} as const;

/**
 * Bounds for the numeric properties that decide how much Vega materializes before a single row is
 * drawn. `axis.tickCount` and `legend.tickCount` share the guide implementation, so a count of two
 * million asks Vega to lay out two million tick marks, and `bin.maxbins` decides how many bins a
 * transform produces. The limits are deliberately far above what a readable chart needs - a few
 * hundred ticks or bins - so ordinary axes, legends and the widget templates are unaffected.
 */
export const VEGA_LITE_CARDINALITY_LIMITS = {
  maxBins: VEGA_LITE_MAX_RESOURCE_COUNT,
  maxTickCount: VEGA_LITE_MAX_RESOURCE_COUNT,
  minBins: 1,
  minTickCount: 0,
} as const;

/** JSON Pointer of the offending value. `/` denotes the whole definition. */
export type VegaLiteSpecIssue = {
  message: string;
  path: string;
};

export type VegaLiteSpecValidationResult =
  | { spec: JsonObject; status: 'valid' }
  | { issues: VegaLiteSpecIssue[]; status: 'invalid' };

const ROOT_PATH = '/';

const ALLOWED_ROOT_KEYS = validationMetadata.spec.allowedRootKeys;
const ALLOWED_LAYER_KEYS = validationMetadata.spec.allowedLayerKeys;

const UNSUPPORTED_COMPOSITION_KEYS = ['concat', 'facet', 'hconcat', 'repeat', 'spec', 'vconcat'];

const ALLOWED_MARK_TYPES = validationMetadata.spec.allowedMarkTypes;

const ALLOWED_TRANSFORM_KEYS = [
  'aggregate',
  'bin',
  'calculate',
  'filter',
  'joinaggregate',
  'stack',
  'timeUnit',
  'window',
];

const UNSUPPORTED_TRANSFORM_KEYS = [
  'density',
  'extent',
  'flatten',
  'fold',
  'impute',
  'loess',
  'lookup',
  'pivot',
  'quantile',
  'regression',
  'sample',
];

/** `url` backs image marks and `href` turns marks into links; both fetch or navigate to a resource. */
const UNSUPPORTED_RESOURCE_CHANNELS = ['href', 'url'];

/** Encoding channels that implicitly build a multi-view composition. */
const UNSUPPORTED_COMPOSITION_CHANNELS = ['column', 'facet', 'row'];

const DATA_GENERATOR_KEYS = ['generator', 'graticule', 'sequence', 'sphere'];

/**
 * Properties whose string values Vega compiles as expressions: `calculate` and `filter` on a
 * transform, `test` on a condition, `labelExpr` on an axis or a legend, `toggle` on a point
 * selection, and the `expr` and `signal` references that may appear under any property value.
 */
const EXPRESSION_KEYS = ['calculate', 'expr', 'filter', 'labelExpr', 'signal', 'test', 'toggle'];

/** Logical predicate composition: the operands are expressions in the same context. */
const PREDICATE_COMPOSITION_KEYS = ['and', 'not', 'or'];

/**
 * Selection properties whose string value is a Vega event selector rather than an expression.
 * A selector may carry a filter in brackets - `click[datum.value > 1]` - and Vega compiles that
 * filter and runs it on every matching interaction, so it needs the same policy as `filter`. They
 * are matched at any depth because a selection is configured both under `params` and under
 * `config.selection`; only string values are selectors, so the numeric `axis.translate` offset and
 * the object form of a stream are left to the checks that own them.
 */
const SELECTION_EVENT_KEYS = ['clear', 'on', 'translate', 'zoom'];

/** A value Vega resolves at run time, so the policy cannot bound it while validating. */
const DYNAMIC_VALUE_KEYS = ['expr', 'signal'];

const UNSAFE_OBJECT_KEYS = ['__proto__', 'constructor', 'prototype'];

/**
 * JSON Pointer of a parameter declared at the root or in a layer. Vega-Lite compiles selections
 * only from these declarations; it drops selections in `config.params` and forwards the `bind` of
 * a variable parameter to Vega, which renders it as a form control.
 */
const SELECTION_DECLARATION_PATH = /^(?:\/layer\/\d+)?\/params\/\d+$/;

const LEGEND_BINDING = 'legend';

const UNSUPPORTED_BINDING_MESSAGE = `Input bindings are not supported. Only a point selection declared in the chart or layer "params" can use "bind": "${LEGEND_BINDING}".`;

const URL_VALUE_PATTERN =
  /^\s*(?:(?:https?|ftp|ftps|file|data|blob|javascript|vbscript|ws|wss):|[a-z][a-z0-9+.-]*:\/\/|\/\/)/i;

const ISSUE_PATH_LABELS: Record<string, string> = {
  $schema: 'Schema',
  data: 'Data',
  encoding: 'Encoding',
  layer: 'Layer',
  mark: 'Mark',
  params: 'Parameters',
  transform: 'Transform',
  url: 'URL',
};

function unescapeJsonPointerSegment(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~');
}

function joinPath(parentPath: string, segment: string | number): string {
  const escaped = typeof segment === 'number' ? String(segment) : escapeJsonPointerSegment(segment);

  return parentPath === ROOT_PATH ? `/${escaped}` : `${parentPath}/${escaped}`;
}

function getUtf8ByteLength(text: string): number {
  let length = 0;

  for (const character of text) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (codePoint <= 0x7f) {
      length += 1;
    } else if (codePoint <= 0x7ff) {
      length += 2;
    } else if (codePoint <= 0xffff) {
      length += 3;
    } else {
      length += 4;
    }
  }

  return length;
}

function isPlainJsonObject(value: JsonValue): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatList(values: readonly string[]): string {
  return values.join(', ');
}

function humanizeIssuePath(path: string): string {
  if (path === ROOT_PATH) {
    return 'Definition';
  }

  return path
    .slice(1)
    .split('/')
    .map((segment) => {
      const unescaped = unescapeJsonPointerSegment(segment);

      if (/^\d+$/.test(unescaped)) {
        return `Item ${Number(unescaped) + 1}`;
      }

      return (
        ISSUE_PATH_LABELS[unescaped] ??
        unescaped
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .replace(/^./, (character) => character.toUpperCase())
      );
    })
    .join(' > ');
}

type IssueCollector = {
  add: (path: string, message: string) => void;
  issues: VegaLiteSpecIssue[];
};

function createIssueCollector(): IssueCollector {
  const issues: VegaLiteSpecIssue[] = [];

  return {
    issues,
    add: (path, message) => {
      const isDuplicate = issues.some((issue) => issue.path === path && issue.message === message);

      if (!isDuplicate) {
        issues.push({ message, path });
      }
    },
  };
}

/**
 * Checks every expression Vega would compile from this value. A predicate may be a bare expression
 * string, an array of them, or a logical composition, so the walk follows `and`, `or` and `not`
 * while leaving the remaining predicate properties to the regular structural walk.
 */
function validateExpressionValue(
  value: JsonValue,
  path: string,
  collector: IssueCollector,
  context: VegaLiteCalculateContext,
) {
  if (typeof value === 'string') {
    validateVegaLiteTupleExpression(value, path, context, collector.add);

    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      validateExpressionValue(item, joinPath(path, index), collector, context),
    );
    return;
  }

  if (!isPlainJsonObject(value)) {
    return;
  }

  PREDICATE_COMPOSITION_KEYS.forEach((key) => {
    const operand = value[key];

    if (operand !== undefined) {
      validateExpressionValue(operand, joinPath(path, key), collector, context);
    }
  });
}

/**
 * Checks an event selector. The object form of a stream is left to the structural walk, which
 * reaches its `filter` expressions through `EXPRESSION_KEYS`.
 */
function validateEventSelectorValue(value: JsonValue, path: string, collector: IssueCollector) {
  if (typeof value === 'string') {
    describeVegaLiteExpressionFindings(
      analyzeVegaLiteEventSelector(value),
      VEGA_LITE_DATASET_NAME,
    ).forEach((message) => collector.add(path, message));

    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      validateEventSelectorValue(item, joinPath(path, index), collector),
    );
  }
}

function isDynamicValue(value: JsonValue): boolean {
  return isPlainJsonObject(value) && DYNAMIC_VALUE_KEYS.some((key) => value[key] !== undefined);
}

function getSelectionType(select: JsonValue | undefined): string | undefined {
  if (typeof select === 'string') {
    return select;
  }

  if (select !== undefined && isPlainJsonObject(select) && typeof select.type === 'string') {
    return select.type;
  }

  return undefined;
}

/**
 * `"bind": "legend"` on a point selection makes legend entries toggle the selection, without
 * creating form controls. Input and element bindings, binding maps, legend event streams and
 * `"scales"` stay unsupported.
 */
function isLegendSelectionBinding(param: JsonObject, path: string): boolean {
  return (
    SELECTION_DECLARATION_PATH.test(path) &&
    param.bind === LEGEND_BINDING &&
    getSelectionType(param.select) === 'point'
  );
}

/**
 * Bounds a count that Vega turns into that many objects. A dynamic value is refused outright: its
 * result is only known once the chart runs, so no static bound can hold.
 */
function validateCountValue(
  value: JsonValue,
  path: string,
  collector: IssueCollector,
  name: string,
  minimum: number,
  maximum: number,
) {
  if (isDynamicValue(value)) {
    collector.add(
      path,
      `A computed "${name}" cannot be bounded before the chart runs. Use a whole number between ${minimum} and ${maximum}.`,
    );
    return;
  }

  if (typeof value !== 'number') {
    collector.add(path, `"${name}" must be a whole number between ${minimum} and ${maximum}.`);
    return;
  }

  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    collector.add(
      path,
      `"${name}" must be a whole number between ${minimum} and ${maximum}; this one is ${value}.`,
    );
  }
}

/**
 * `tickCount` also accepts a time interval - `"millisecond"` or `{ "interval": "millisecond",
 * "step": 1 }` - and Vega then derives the tick positions from the scale domain instead of the
 * number, so a millisecond interval over a multi-year domain lays out billions of ticks while the
 * numeric bound never applies. Only a whole number is supported, at any depth and on both guides.
 */
function validateTickCountValue(value: JsonValue, path: string, collector: IssueCollector) {
  const { maxTickCount, minTickCount } = VEGA_LITE_CARDINALITY_LIMITS;

  if (typeof value === 'number') {
    validateCountValue(value, path, collector, 'tickCount', minTickCount, maxTickCount);
    return;
  }

  if (isDynamicValue(value)) {
    collector.add(
      path,
      `A computed "tickCount" cannot be bounded before the chart runs. Use a whole number between ${minTickCount} and ${maxTickCount}.`,
    );
    return;
  }

  collector.add(
    path,
    `"tickCount" must be a whole number between ${minTickCount} and ${maxTickCount}. Time interval tick counts such as "millisecond" or { "interval": "day", "step": 1 } are not supported because the number of ticks follows the data.`,
  );
}

/**
 * Bin cardinality controls. `step`, `steps` and `minstep` set the bin width instead of the bin
 * count, so the number of bins follows the domain of the data and `maxbins` no longer caps it: a
 * step of `0.000001` over a domain of a few thousand asks the binning pass for billions of bins.
 * These three properties are refused wherever they configure binning - a transform, an encoding
 * channel, `config.bin` or an object `scale.bins`.
 */
const BIN_CARDINALITY_KEYS = ['minstep', 'step', 'steps'];

const BIN_PROGRESS_LIMITS = {
  minBase: 1.5,
  maxBase: 100,
  minExtentSpan: 1e-100,
  maxExtentMagnitude: 1e100,
};

function validateBinExtent(value: JsonValue, path: string, collector: IssueCollector) {
  const { minExtentSpan, maxExtentMagnitude } = BIN_PROGRESS_LIMITS;

  if (
    !Array.isArray(value) ||
    value.length !== 2 ||
    value.some(
      (bound) =>
        typeof bound !== 'number' ||
        !Number.isFinite(bound) ||
        Math.abs(bound) > maxExtentMagnitude,
    ) ||
    typeof value[0] !== 'number' ||
    typeof value[1] !== 'number' ||
    value[1] - value[0] < minExtentSpan
  ) {
    collector.add(
      path,
      `Bin "extent" must be two increasing finite numeric literals between -${maxExtentMagnitude} and ${maxExtentMagnitude}, separated by at least ${minExtentSpan}. Omit "extent" to use the data domain.`,
    );
  }
}

function validateBinParams(value: JsonValue, path: string, collector: IssueCollector) {
  if (!isPlainJsonObject(value)) {
    return;
  }

  // Bound custom magnitudes rather than reproduce Vega's logarithms and powers. Together with
  // the extent bounds, these keep the initial step far from underflow and multiplication progressing.
  const base = value.base;
  const { minBase, maxBase } = BIN_PROGRESS_LIMITS;

  if (isDynamicValue(base)) {
    collector.add(
      joinPath(path, 'base'),
      `A computed bin "base" cannot be checked before the chart runs. Use a finite numeric literal from ${minBase} to ${maxBase}.`,
    );
  } else if (
    base !== undefined &&
    (typeof base !== 'number' || !Number.isFinite(base) || base < minBase || base > maxBase)
  ) {
    collector.add(
      joinPath(path, 'base'),
      `Bin "base" must be a finite numeric literal from ${minBase} to ${maxBase}${
        typeof base === 'number' ? `; this one is ${base}` : ''
      }.`,
    );
  }

  if (value.span !== undefined) {
    collector.add(
      joinPath(path, 'span'),
      'Custom bin "span" is not supported because the computed step can underflow and prevent progress. Omit "span" and use "maxbins" instead.',
    );
  }

  if (value.extent !== undefined) {
    validateBinExtent(value.extent, joinPath(path, 'extent'), collector);
  }

  BIN_CARDINALITY_KEYS.forEach((key) => {
    if (value[key] === undefined) {
      return;
    }

    collector.add(
      joinPath(path, key),
      `Bin "${key}" sets the bin width, so the number of bins follows the data and cannot be bounded. Use "maxbins" with a whole number between ${VEGA_LITE_CARDINALITY_LIMITS.minBins} and ${VEGA_LITE_CARDINALITY_LIMITS.maxBins}.`,
    );
  });
}

/**
 * `scale.bins` accepts explicit boundaries or an object that generates them from a step size. The
 * generated form has the same unbounded cardinality as a bin step, so only the explicit list is
 * supported.
 */
function validateScaleBinsValue(value: JsonValue, path: string, collector: IssueCollector) {
  if (!isPlainJsonObject(value)) {
    return;
  }

  collector.add(
    path,
    'A generated "bins" object sets the bin width, so the number of bins follows the data and cannot be bounded. List the bin boundaries explicitly, such as "bins": [0, 10, 20].',
  );
}

/**
 * Numeric controls are matched by name at any depth, because `tickCount` sits on an axis, on a
 * legend and inside a `config` block, and `maxbins` sits on a transform and on an encoding. The bin
 * width controls are matched through their container instead, so the `step` that sizes a discrete
 * `width` or `height` - which the renderer replaces with the measured host size anyway - stays
 * usable.
 */
function validateResourceControlValue(
  key: string,
  value: JsonValue,
  path: string,
  parentPath: string,
  collector: IssueCollector,
) {
  if (key === 'tickCount') {
    validateTickCountValue(value, path, collector);
    return;
  }

  if (key === 'maxbins') {
    validateCountValue(
      value,
      path,
      collector,
      'maxbins',
      VEGA_LITE_CARDINALITY_LIMITS.minBins,
      VEGA_LITE_CARDINALITY_LIMITS.maxBins,
    );
    return;
  }

  if (key === 'bin') {
    validateBinParams(value, path, collector);
    return;
  }

  if (key === 'bins' && parentPath.endsWith('/scale')) {
    validateScaleBinsValue(value, path, collector);
  }
}

function validateNumericFormatValue(
  value: JsonValue,
  path: string,
  collector: IssueCollector,
  allowTimeFormat = false,
) {
  if (
    allowTimeFormat &&
    isPlainJsonObject(value) &&
    !isDynamicValue(value) &&
    Object.values(value).every((format) => typeof format === 'string')
  ) {
    return;
  }

  const issue = getVegaLiteNumericFormatIssue(value);

  // Time formats use a different grammar. Still bound anything that parses as a numeric format,
  // including percent widths, so a temporal declaration cannot disguise numeric padding.
  if (issue && !(allowTimeFormat && issue.kind === 'syntax')) {
    collector.add(path, issue.message);
  }
}

/**
 * Rules that must hold at every depth, so a nested definition cannot reintroduce a resource,
 * a second dataset or an executable hook that the structural checks only look for at the top.
 */
function walkSpecValues(
  value: JsonValue,
  path: string,
  collector: IssueCollector,
  timeFormat = false,
  calculateContext: VegaLiteCalculateContext = { fields: new Map(), budget: { allocation: 0 } },
  isOrderedTransform = false,
) {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      walkSpecValues(item, joinPath(path, index), collector, timeFormat, calculateContext),
    );
    return;
  }

  if (!isPlainJsonObject(value)) {
    const isNamedResourceValue = UNSUPPORTED_RESOURCE_CHANNELS.some((key) =>
      path.endsWith(`/${key}`),
    );

    if (
      typeof value === 'string' &&
      path !== '/$schema' &&
      !isNamedResourceValue &&
      URL_VALUE_PATTERN.test(value)
    ) {
      collector.add(
        path,
        'External references are not supported. Remove the URI and use the Neptune dataset instead.',
      );
    }

    return;
  }

  const usesTimeFormat =
    timeFormat ||
    value.type === 'temporal' ||
    value.timeUnit !== undefined ||
    (isPlainJsonObject(value.scale) && (value.scale.type === 'time' || value.scale.type === 'utc'));

  const scopedContext = { ...calculateContext, fields: new Map(calculateContext.fields) };
  const transformContexts = new Map<string, VegaLiteCalculateContext>();

  if (Array.isArray(value.transform)) {
    validateVegaLiteCalculateTransforms(
      value.transform,
      joinPath(path, 'transform'),
      scopedContext,
      collector.add,
      (_transform, transformPath, context) =>
        transformContexts.set(transformPath, { ...context, fields: new Map(context.fields) }),
    );
  }

  Object.entries(value).forEach(([key, child]) => {
    const childPath = joinPath(path, key);

    if (UNSAFE_OBJECT_KEYS.includes(key)) {
      collector.add(childPath, `Remove the unsafe object key "${key}".`);
      return;
    }

    if (key === 'url' || key === 'href') {
      collector.add(
        childPath,
        `The "${key}" property loads or links to an external resource and is not supported.`,
      );
    }

    if (key === 'data' && childPath !== '/data') {
      collector.add(
        childPath,
        `Only the fixed "${VEGA_LITE_DATASET_NAME}" dataset is available. Remove the nested data definition.`,
      );
    }

    if (key === 'datasets') {
      collector.add(
        childPath,
        `Inline datasets are not supported. Chart data always comes from the "${VEGA_LITE_DATASET_NAME}" dataset.`,
      );
    }

    if (key === 'values' && path.endsWith('/data')) {
      collector.add(childPath, 'Inline data values are not supported.');
    }

    if (DATA_GENERATOR_KEYS.includes(key)) {
      collector.add(childPath, `Data generators are not supported: "${key}".`);
    }

    if (key === 'formatType' || key === 'customFormatTypes') {
      collector.add(
        childPath,
        'Custom format types require registering executable functions and are not supported.',
      );
    }

    if (key === 'expressionFunctions' || key === 'signals' || key === 'loader') {
      collector.add(childPath, `Custom runtime hooks are not supported: "${key}".`);
    }

    if (key === 'usermeta') {
      collector.add(
        childPath,
        'Definition-provided embed options are not supported. Remove "usermeta".',
      );
    }

    if (key === 'bind' && !isLegendSelectionBinding(value, path)) {
      collector.add(childPath, UNSUPPORTED_BINDING_MESSAGE);
    }

    if (EXPRESSION_KEYS.includes(key) && !(key === 'calculate' && isOrderedTransform)) {
      validateExpressionValue(child, childPath, collector, scopedContext);
    }

    if (SELECTION_EVENT_KEYS.includes(key)) {
      validateEventSelectorValue(child, childPath, collector);
    }

    validateResourceControlValue(key, child, childPath, path, collector);

    if (key === 'numberFormat' || key === 'normalizedNumberFormat') {
      validateNumericFormatValue(child, childPath, collector);
    } else if (key === 'format') {
      const configuredTimeFormat =
        path.startsWith('/config/') &&
        ((typeof child === 'string' && /%[-_0]?[a-z%]/i.test(child)) || isPlainJsonObject(child));

      validateNumericFormatValue(
        child,
        childPath,
        collector,
        usesTimeFormat || configuredTimeFormat,
      );
    }

    if (key === 'transform' && Array.isArray(child)) {
      child.forEach((transform, index) => {
        const transformPath = joinPath(childPath, index);
        walkSpecValues(
          transform,
          transformPath,
          collector,
          usesTimeFormat,
          transformContexts.get(transformPath) ?? scopedContext,
          true,
        );
      });
    } else {
      walkSpecValues(child, childPath, collector, usesTimeFormat, scopedContext);
    }
  });
}

function validateData(spec: JsonObject, collector: IssueCollector) {
  const data = spec.data;

  if (data === undefined) {
    return;
  }

  if (!isPlainJsonObject(data)) {
    collector.add('/data', `Data must be { "name": "${VEGA_LITE_DATASET_NAME}" }.`);
    return;
  }

  const keys = Object.keys(data);
  const extraKeys = keys.filter(
    (key) => key !== 'name' && !UNSUPPORTED_RESOURCE_CHANNELS.includes(key),
  );

  if (data.name !== VEGA_LITE_DATASET_NAME) {
    collector.add(
      '/data',
      `Chart data must be bound to the "${VEGA_LITE_DATASET_NAME}" dataset. Use { "name": "${VEGA_LITE_DATASET_NAME}" } or omit "data".`,
    );
  }

  if (extraKeys.length > 0) {
    collector.add('/data', `Remove unsupported data properties: ${formatList(extraKeys)}.`);
  }
}

function validateMark(mark: JsonValue, path: string, collector: IssueCollector) {
  if (typeof mark === 'string') {
    if (!ALLOWED_MARK_TYPES.includes(mark)) {
      collector.add(
        path,
        `Mark "${mark}" is not supported. Supported marks: ${formatList(ALLOWED_MARK_TYPES)}.`,
      );
    }

    return;
  }

  if (!isPlainJsonObject(mark)) {
    collector.add(path, 'Mark must be a mark name or a mark definition object.');
    return;
  }

  const markType = mark.type;

  if (typeof markType !== 'string') {
    collector.add(joinPath(path, 'type'), 'Mark definitions require a "type".');
    return;
  }

  if (!ALLOWED_MARK_TYPES.includes(markType)) {
    collector.add(
      joinPath(path, 'type'),
      `Mark "${markType}" is not supported. Supported marks: ${formatList(ALLOWED_MARK_TYPES)}.`,
    );
  }
}

function validateEncoding(encoding: JsonValue, path: string, collector: IssueCollector) {
  if (!isPlainJsonObject(encoding)) {
    collector.add(path, 'Encoding must be an object.');
    return;
  }

  Object.keys(encoding).forEach((channel) => {
    if (UNSUPPORTED_RESOURCE_CHANNELS.includes(channel)) {
      collector.add(
        joinPath(path, channel),
        `The "${channel}" channel loads external resources and is not supported.`,
      );
    }

    if (UNSUPPORTED_COMPOSITION_CHANNELS.includes(channel)) {
      collector.add(
        joinPath(path, channel),
        `The "${channel}" channel builds a multi-view composition and is not supported. Use a single unit or layered definition.`,
      );
    }
  });
}

function countTransforms(transform: JsonValue, path: string, collector: IssueCollector): number {
  if (transform === undefined) {
    return 0;
  }

  if (!Array.isArray(transform)) {
    collector.add(path, 'Transform must be an array of transform definitions.');
    return 0;
  }

  transform.forEach((entry, index) => {
    const entryPath = joinPath(path, index);

    if (!isPlainJsonObject(entry)) {
      collector.add(entryPath, 'Each transform must be an object.');
      return;
    }

    const keys = Object.keys(entry);

    if (keys.filter((key) => ALLOWED_TRANSFORM_KEYS.includes(key)).length > 1) {
      collector.add(entryPath, 'Each transform definition must have exactly one transform kind.');
    }

    const unsupportedKeys = keys.filter((key) => UNSUPPORTED_TRANSFORM_KEYS.includes(key));

    if (unsupportedKeys.length > 0) {
      collector.add(
        entryPath,
        `Unsupported transform: ${formatList(unsupportedKeys)}. Supported transforms: ${formatList(
          ALLOWED_TRANSFORM_KEYS,
        )}.`,
      );
      return;
    }

    if (!keys.some((key) => ALLOWED_TRANSFORM_KEYS.includes(key))) {
      collector.add(
        entryPath,
        `Unsupported transform. Supported transforms: ${formatList(ALLOWED_TRANSFORM_KEYS)}.`,
      );
    }
  });

  return transform.length;
}

/**
 * Vega-Lite forwards variable parameters to Vega signals, with `expr` becoming `update`.
 * Even a literal expression can override host sizing, so checking its function calls is not
 * enough. Refuse computed and raw signal hooks while keeping fixed values and selections.
 */
const PARAM_SIGNAL_HOOKS: Record<string, string> = {
  expr: 'Parameter "expr" is a computed signal expression and is not supported. Use "value" for a fixed value, or a transform to derive values from the chart rows.',
  init: 'Parameter "init" is a raw signal expression and is not supported. Use "value" for a starting value.',
  on: 'Parameter "on" defines raw signal event handlers and is not supported. Use "select" with a "point" or "interval" selection for interaction.',
  update:
    'Parameter "update" is a raw signal expression and is not supported. Use "value" for a fixed value, or a transform to derive values from the chart rows.',
};

/**
 * Vega merges the view-property signals over host settings. `cursor` controls the view's CSS
 * cursor, while `datum`, `event`, `item` and `parent` belong to its expression/group scope.
 */
const RESERVED_PARAM_NAMES = [
  'autosize',
  'background',
  'cursor',
  'datum',
  'event',
  'height',
  'item',
  'padding',
  'parent',
  'width',
];

function validateParams(params: JsonValue, path: string, collector: IssueCollector) {
  if (params === undefined) {
    return;
  }

  if (!Array.isArray(params)) {
    collector.add(path, 'Params must be an array of parameter definitions.');
    return;
  }

  params.forEach((param, index) => {
    const paramPath = joinPath(path, index);

    if (!isPlainJsonObject(param)) {
      collector.add(paramPath, 'Each parameter must be an object.');
      return;
    }

    if (typeof param.name !== 'string' || param.name.length === 0) {
      collector.add(joinPath(paramPath, 'name'), 'Each parameter requires a name.');
    } else if (RESERVED_PARAM_NAMES.includes(param.name)) {
      collector.add(
        joinPath(paramPath, 'name'),
        `Parameter name "${param.name}" is reserved by the Vega runtime. Use a different name, such as "cutoff".`,
      );
    }

    Object.entries(PARAM_SIGNAL_HOOKS).forEach(([key, message]) => {
      if (param[key] !== undefined) {
        collector.add(joinPath(paramPath, key), message);
      }
    });

    if (param.select === undefined) {
      return;
    }

    const selectType = getSelectionType(param.select);

    if (selectType !== 'point' && selectType !== 'interval') {
      collector.add(
        joinPath(paramPath, 'select'),
        'Only "point" and "interval" selections are supported.',
      );
    }
  });
}

/**
 * Parameter declarations belong to specs and their config, not arbitrary JSON in fixed values.
 * Follow only composition edges here; walkSpecValues still checks every value for other hazards.
 */
function validateSpecParams(spec: JsonObject, path: string, collector: IssueCollector) {
  validateParams(spec.params, joinPath(path, 'params'), collector);

  if (isPlainJsonObject(spec.config)) {
    validateParams(spec.config.params, joinPath(joinPath(path, 'config'), 'params'), collector);
  }

  if (isPlainJsonObject(spec.spec)) {
    validateSpecParams(spec.spec, joinPath(path, 'spec'), collector);
  }

  ['concat', 'hconcat', 'layer', 'vconcat'].forEach((key) => {
    const children = spec[key];

    if (!Array.isArray(children)) {
      return;
    }

    const childrenPath = joinPath(path, key);

    children.forEach((child, index) => {
      if (isPlainJsonObject(child)) {
        validateSpecParams(child, joinPath(childrenPath, index), collector);
      }
    });
  });
}

function validateUnit(unit: JsonObject, path: string, collector: IssueCollector): number {
  if (unit.mark === undefined) {
    collector.add(path, 'Each layer requires a "mark".');
  } else {
    validateMark(unit.mark, joinPath(path, 'mark'), collector);
  }

  if (unit.encoding !== undefined) {
    validateEncoding(unit.encoding, joinPath(path, 'encoding'), collector);
  }

  return countTransforms(unit.transform, joinPath(path, 'transform'), collector);
}

function validateLayers(layer: JsonValue, collector: IssueCollector): number {
  if (!Array.isArray(layer)) {
    collector.add('/layer', 'Layer must be an array of unit definitions.');
    return 0;
  }

  if (layer.length === 0) {
    collector.add('/layer', 'Layer must contain at least one unit definition.');
    return 0;
  }

  if (layer.length > VEGA_LITE_SPEC_LIMITS.maxLayers) {
    collector.add(
      '/layer',
      `A definition must not exceed ${VEGA_LITE_SPEC_LIMITS.maxLayers} layers; this one has ${layer.length}.`,
    );
  }

  let transformCount = 0;

  layer.forEach((child, index) => {
    const childPath = joinPath('/layer', index);

    if (!isPlainJsonObject(child)) {
      collector.add(childPath, 'Each layer must be an object.');
      return;
    }

    const unsupportedKeys = Object.keys(child).filter((key) => !ALLOWED_LAYER_KEYS.includes(key));

    if (unsupportedKeys.length > 0) {
      collector.add(
        childPath,
        `Unsupported layer properties: ${formatList(
          unsupportedKeys,
        )}. Supported properties: ${formatList(ALLOWED_LAYER_KEYS)}.`,
      );
    }

    transformCount += validateUnit(child, childPath, collector);
  });

  return transformCount;
}

function validateComposition(spec: JsonObject, collector: IssueCollector): number {
  const unsupportedComposition = Object.keys(spec).filter((key) =>
    UNSUPPORTED_COMPOSITION_KEYS.includes(key),
  );

  if (unsupportedComposition.length > 0) {
    collector.add(
      ROOT_PATH,
      `Multi-view composition is not supported: ${formatList(
        unsupportedComposition,
      )}. Use a single unit or layered definition.`,
    );
  }

  const hasMark = spec.mark !== undefined;
  const hasLayer = spec.layer !== undefined;

  if (spec.encoding !== undefined) {
    validateEncoding(spec.encoding, '/encoding', collector);
  }

  if (hasMark && hasLayer) {
    collector.add(ROOT_PATH, 'A definition must use either "mark" or "layer", not both.');
    return 0;
  }

  if (!hasMark && !hasLayer) {
    if (unsupportedComposition.length === 0) {
      collector.add(ROOT_PATH, 'A definition requires a "mark" or a "layer".');
    }

    return 0;
  }

  if (hasLayer) {
    return validateLayers(spec.layer, collector);
  }

  validateMark(spec.mark, '/mark', collector);

  return 0;
}

function validateSchema(spec: JsonObject, collector: IssueCollector) {
  const schema = spec.$schema;

  if (schema === undefined) {
    return;
  }

  if (schema !== VEGA_LITE_SCHEMA_URL) {
    collector.add(
      '/$schema',
      `Only the Vega-Lite v6 schema is supported. Use "${VEGA_LITE_SCHEMA_URL}" or omit "$schema".`,
    );
  }
}

function validateRootKeys(spec: JsonObject, collector: IssueCollector) {
  const unsupportedKeys = Object.keys(spec).filter(
    (key) => !ALLOWED_ROOT_KEYS.includes(key) && !UNSUPPORTED_COMPOSITION_KEYS.includes(key),
  );

  if (unsupportedKeys.length > 0) {
    collector.add(
      ROOT_PATH,
      `Unsupported definition properties: ${formatList(
        unsupportedKeys,
      )}. Supported properties: ${formatList(ALLOWED_ROOT_KEYS)}.`,
    );
  }
}

/**
 * Binds the fixed Neptune dataset and pins the schema. This is the only repair the policy performs,
 * and only once every other rule already holds, so a rejected definition is never partly rewritten.
 */
function bindNeptuneDataset(spec: JsonObject): JsonObject {
  const { $schema: _schema, data: _data, ...rest } = spec;

  return {
    $schema: VEGA_LITE_SCHEMA_URL,
    data: { name: VEGA_LITE_DATASET_NAME },
    ...rest,
  };
}

export function validateVegaLiteSpec(value: unknown): VegaLiteSpecValidationResult {
  const validation = validateVegaLiteJsonTree(value);

  if (validation.status === 'invalid') {
    return { issues: [validation.issue], status: 'invalid' };
  }

  const candidate = validation.value;
  const collector = createIssueCollector();

  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
    collector.add(ROOT_PATH, 'The definition must be a JSON object.');
    return { issues: collector.issues, status: 'invalid' };
  }

  const specBytes = getUtf8ByteLength(JSON.stringify(candidate));

  // An oversized definition is rejected before the structural walk so validation cost stays bounded
  // no matter how large the pasted document is.
  if (specBytes > VEGA_LITE_SPEC_LIMITS.maxSpecBytes) {
    collector.add(
      ROOT_PATH,
      `The definition must not exceed ${VEGA_LITE_SPEC_LIMITS.maxSpecBytes} bytes; this one is ${specBytes} bytes.`,
    );
    return { issues: collector.issues, status: 'invalid' };
  }

  validateRootKeys(candidate, collector);
  validateSchema(candidate, collector);
  validateData(candidate, collector);

  const rootTransformCount = countTransforms(candidate.transform, '/transform', collector);
  const layerTransformCount = validateComposition(candidate, collector);
  const transformCount = rootTransformCount + layerTransformCount;

  if (transformCount > VEGA_LITE_SPEC_LIMITS.maxTransforms) {
    collector.add(
      ROOT_PATH,
      `A definition must not exceed ${VEGA_LITE_SPEC_LIMITS.maxTransforms} transforms; this one has ${transformCount}.`,
    );
  }

  validateSpecParams(candidate, ROOT_PATH, collector);
  walkSpecValues(candidate, ROOT_PATH, collector);

  if (collector.issues.length > 0) {
    return { issues: collector.issues, status: 'invalid' };
  }

  return { spec: bindNeptuneDataset(candidate), status: 'valid' };
}

/**
 * Parses editable JSON text and applies the same policy. Parse failures are reported at the root so
 * the editor can show them next to policy issues instead of a separate error channel.
 */
export function parseVegaLiteSpec(text: string): VegaLiteSpecValidationResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown parse error.';

    return {
      issues: [{ message: `The definition is not valid JSON: ${message}`, path: ROOT_PATH }],
      status: 'invalid',
    };
  }

  return validateVegaLiteSpec(parsed);
}

export function formatVegaLiteSpecIssue(issue: VegaLiteSpecIssue): string {
  const location = humanizeIssuePath(issue.path);

  return issue.path === ROOT_PATH
    ? `${location}: ${issue.message}`
    : `${location} (${issue.path}): ${issue.message}`;
}
